import React from 'react';
import { View, Text, TouchableOpacity, ScrollView } from 'react-native';
import { useAuthContext } from '../../auth';
import { useAccessControl } from '../../hooks/useAccessControl';

interface NavLinkProps {
  label: string;
  href: string;
  onPress?: () => void;
}

interface MainNavProps {
  onNavigate?: (route: string) => void;
  onLogout?: () => void;
}

export const MainNavNative = ({ onNavigate, onLogout }: MainNavProps) => {
  const { user, logout } = useAuthContext();
  const { isAdmin } = useAccessControl();
  const [expandedMenu, setExpandedMenu] = React.useState<string | null>(null);

  const publicNavLinks: NavLinkProps[] = [
    { label: 'Home', href: '/' },
    { label: 'Blog', href: '/blog' },
  ];

  const authNavLinks: NavLinkProps[] = [
    { label: 'Home', href: '/' },
    { label: 'Blog', href: '/blog' },
    { label: 'Profile', href: '/profile' },
    { label: 'Job Queue', href: '/jobs' },
  ];

  const navLinks = user ? authNavLinks : publicNavLinks;

  const handleNavigation = (href: string) => {
    if (onNavigate) {
      onNavigate(href);
    }
    setExpandedMenu(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      if (onLogout) {
        onLogout();
      }
      if (onNavigate) {
        onNavigate('/');
      }
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  return (
    <View style={styles.container}>
      {/* Top Bar */}
      <View style={styles.topBar}>
        <TouchableOpacity onPress={() => handleNavigation('/')} style={styles.brandButton}>
          <Text style={styles.brandText}>TaskMaster</Text>
        </TouchableOpacity>
      </View>

      {/* Navigation Links */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.linksContainer}>
        <View style={styles.linksRow}>
          {navLinks.map((link) => (
            <TouchableOpacity 
              key={link.href} 
              onPress={() => handleNavigation(link.href)}
              style={styles.linkButton}
            >
              <Text style={styles.linkText}>{link.label}</Text>
            </TouchableOpacity>
          ))}

          {/* Admin Menu */}
          {isAdmin && (
            <TouchableOpacity 
              onPress={() => setExpandedMenu(expandedMenu === 'admin' ? null : 'admin')}
              style={styles.linkButton}
            >
              <Text style={styles.linkText}>⚙️ Admin</Text>
            </TouchableOpacity>
          )}
        </View>
      </ScrollView>

      {/* Admin Submenu */}
      {isAdmin && expandedMenu === 'admin' && (
        <View style={styles.submenu}>
          <TouchableOpacity 
            onPress={() => handleNavigation('/admin')}
            style={styles.submenuItem}
          >
            <Text style={styles.submenuText}>Admin Panel</Text>
          </TouchableOpacity>
          <TouchableOpacity 
            onPress={() => handleNavigation('/admin-debug')}
            style={styles.submenuItem}
          >
            <Text style={styles.submenuText}>Debug Tools</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* User Section */}
      <View style={styles.userSection}>
        {user ? (
          <View style={styles.userContainer}>
            <View style={styles.userInfo}>
              <View style={styles.avatar}>
                <Text style={styles.avatarText}>
                  {user.email?.charAt(0).toUpperCase()}
                </Text>
              </View>
              <Text style={styles.userEmail}>{user.email?.split('@')[0]}</Text>
            </View>
            <TouchableOpacity 
              onPress={handleLogout}
              style={styles.logoutButton}
            >
              <Text style={styles.logoutText}>Logout</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <View style={styles.authButtons}>
            <TouchableOpacity 
              onPress={() => handleNavigation('/login')}
              style={styles.loginButton}
            >
              <Text style={styles.loginText}>Login</Text>
            </TouchableOpacity>
            <TouchableOpacity 
              onPress={() => handleNavigation('/signup')}
              style={styles.signupButton}
            >
              <Text style={styles.signupText}>Sign Up</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>
    </View>
  );
};

const styles = {
  container: {
    backgroundColor: '#ffffff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e6ed',
    paddingTop: 40,
  },
  topBar: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e6ed',
  },
  brandButton: {
    padding: 4,
  },
  brandText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#2a7cff',
  },
  linksContainer: {
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e6ed',
  },
  linksRow: {
    flexDirection: 'row',
    gap: 8,
    paddingVertical: 8,
  },
  linkButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  linkText: {
    fontSize: 14,
    color: '#2c303b',
    fontWeight: '500',
  },
  submenu: {
    backgroundColor: '#f8f9fa',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e6ed',
  },
  submenuItem: {
    paddingVertical: 8,
    paddingHorizontal: 8,
  },
  submenuText: {
    fontSize: 13,
    color: '#2a7cff',
    fontWeight: '500',
  },
  userSection: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: '#e0e6ed',
  },
  userContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  userInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#2a7cff',
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarText: {
    color: '#ffffff',
    fontWeight: 'bold',
    fontSize: 14,
  },
  userEmail: {
    fontSize: 13,
    color: '#2c303b',
    fontWeight: '500',
  },
  logoutButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    backgroundColor: '#fee2e2',
  },
  logoutText: {
    fontSize: 12,
    color: '#dc2626',
    fontWeight: '600',
  },
  authButtons: {
    flexDirection: 'row',
    gap: 8,
    justifyContent: 'flex-end',
  },
  loginButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#2a7cff',
  },
  loginText: {
    fontSize: 12,
    color: '#2a7cff',
    fontWeight: '600',
  },
  signupButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    backgroundColor: '#2a7cff',
  },
  signupText: {
    fontSize: 12,
    color: '#ffffff',
    fontWeight: '600',
  },
} as const;
